import { useEffect, useRef, useState } from 'react';
import { Table } from 'antd';
import { useTranslation } from 'react-i18next';
import type { TableProps, PaginationProps } from 'antd';

interface PaginatedTableProps<RecordType> extends TableProps<RecordType> {
    total: number;
    current: number;
    pageSize: number;
    rowKey?: string;
    tableId?: string;
    onPaginationChange?: PaginationProps['onChange'];
    onPageSizeChange?: PaginationProps['onShowSizeChange'];
    onRow?: TableProps<RecordType>['onRow'];
    rowSelection?: TableProps<RecordType>['rowSelection'];
}

const DragHeaderCell = (props: any) => {
    const { onDragStart, onDragOver, onDrop, onDragEnter, onDragLeave, draggable, style, ...rest } = props;
    const [over, setOver] = useState(false);

    return (
        <th
            {...rest}
            draggable={draggable}
            onDragStart={onDragStart}
            onDragOver={(e) => { e.preventDefault(); setOver(true); onDragOver?.(e); }}
            onDragEnter={(e) => { e.preventDefault(); setOver(true); onDragEnter?.(e); }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => { setOver(false); onDrop?.(e); }}
            style={{
                ...style,
                cursor: draggable ? 'grab' : undefined,
                background: over ? '#e6f0fb' : style?.background,
                transition: 'background .15s',
            }}
        />
    );
};

const LS_PREFIX = 'paginatedTable_colOrder_';

function PaginatedTable<RecordType extends object>({
    dataSource,
    columns,
    scroll = { x: 1200 },
    rowKey = 'id',
    tableId,
    onChange,
    total,
    current,
    pageSize,
    pagination,
    onPaginationChange,
    onPageSizeChange,
    onRow,
    rowSelection,
    ...rest
}: PaginatedTableProps<RecordType>) {
    const { t } = useTranslation();
    const colKey = (c: any) => String(c.key ?? c.dataIndex ?? '');

    const [colKeys, setColKeys] = useState<string[]>(() => {
        if (tableId) {
            try {
                const saved = localStorage.getItem(LS_PREFIX + tableId);
                if (saved) return JSON.parse(saved);
            } catch {}
        }
        return [];
    });

    const dragKey = useRef<string | null>(null);

    // Persist order whenever it changes
    useEffect(() => {
        if (tableId && colKeys.length > 0) {
            localStorage.setItem(LS_PREFIX + tableId, JSON.stringify(colKeys));
        }
    }, [tableId, colKeys]);

    // Merge saved/current order with incoming columns (handles visibility toggles)
    useEffect(() => {
        const newKeys = (columns ?? []).map(colKey);
        setColKeys(prev => {
            const kept  = prev.filter(k => newKeys.includes(k));
            const added = newKeys.filter(k => !prev.includes(k));
            return [...kept, ...added];
        });
    }, [columns]);

    const sortedColumns = colKeys
        .map(k => (columns ?? []).find((c: any) => colKey(c) === k))
        .filter(Boolean) as typeof columns;

    const draggableColumns = (sortedColumns ?? []).map((col: any) => {
        const fixed = col.fixed;
        return {
            ...col,
            onHeaderCell: () => ({
                draggable: !fixed,
                onDragStart: () => { dragKey.current = colKey(col); },
                onDragOver:  (e: DragEvent) => e.preventDefault(),
                onDrop:      () => {
                    const from = dragKey.current;
                    const to   = colKey(col);
                    if (!from || from === to) return;
                    setColKeys(prev => {
                        const arr = [...prev];
                        const fi  = arr.indexOf(from);
                        const ti  = arr.indexOf(to);
                        if (fi === -1 || ti === -1) return prev;
                        arr.splice(fi, 1);
                        arr.splice(ti, 0, from);
                        return arr;
                    });
                    dragKey.current = null;
                },
            }),
        };
    });

    return (
        <Table<RecordType>
            dataSource={dataSource}
            columns={draggableColumns as typeof columns}
            scroll={scroll}
            rowKey={rowKey}
            onChange={onChange}
            onRow={onRow}
            rowSelection={rowSelection}
            components={{ header: { cell: DragHeaderCell } }}
            pagination={{
                ...pagination,
                total,
                current,
                pageSize,
                showSizeChanger: true,
                onChange: onPaginationChange,
                onShowSizeChange: onPageSizeChange,
                position: ['topLeft', 'bottomLeft'],
                showTotal: (total) => (
                    <>
                        {t('PaginateTotal')} <strong>{total}</strong> {t('PaginateRegisters')}
                    </>
                ),
            }}
            {...rest}
        />
    );
}

export default PaginatedTable;
